import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { IconRefresh } from '../components/Icons';
import './AdminSiteMetrics.css';

/**
 * Site metrics (Full Admin): who uses RiskLo, how much, on which pages, by tier.
 * Built from one row per person per day (services/siteActivityService.js), so
 * it never touches Whop or per-request logs. The server caches it a minute.
 */

const RANGES = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const TIER_LABEL = {
  admin: 'Admin', support: 'Support', staff: 'Brand Admin', sales: 'Sales', max: 'Max', pro: 'Pro', basic: 'Basic', unknown: 'Unknown',
};
const TIER_FILTERS = ['', 'sales', 'max', 'pro', 'basic', 'staff', 'support', 'admin'];

const num = (n) => Number(n || 0).toLocaleString('en-US');
function minutesText(m) {
  const v = Number(m) || 0;
  if (v < 60) return `${v} min`;
  const h = Math.floor(v / 60);
  const r = v % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}
function ago(iso) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '—';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
const shortDay = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dateText = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const pageName = (p) => String(p || '').replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function Stat({ label, value, sub }) {
  return (
    <div className="asm-stat">
      <span>{label}</span>
      <b>{value}</b>
      {sub ? <small>{sub}</small> : null}
    </div>
  );
}

function DayTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const tiers = Object.entries(d.tiers || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="asm-tip">
      <strong>{shortDay(d.day)}</strong>
      <div>{num(d.people)} active · {minutesText(d.minutes)}</div>
      {d.signups ? <div>{num(d.signups)} new sign-up{d.signups === 1 ? '' : 's'}</div> : null}
      {tiers.length ? <div className="asm-tip-tiers">{tiers.map(([t, n]) => `${TIER_LABEL[t] || t} ${n}`).join(' · ')}</div> : null}
    </div>
  );
}

/** A ranked list with a bar per row (tiers, pages): one hue, labels in text ink. */
function BarList({ rows, valueOf, labelOf, detailOf }) {
  const max = Math.max(1, ...rows.map(valueOf));
  return (
    <ul className="asm-barlist">
      {rows.map((r) => (
        <li key={labelOf(r)} title={`${labelOf(r)}: ${detailOf(r)}`}>
          <span className="asm-barlist-label">{labelOf(r)}</span>
          <span className="asm-barlist-track"><i style={{ width: `${Math.max(2, (valueOf(r) / max) * 100)}%` }} /></span>
          <span className="asm-barlist-value">{detailOf(r)}</span>
        </li>
      ))}
    </ul>
  );
}

function PersonDetail({ person, days, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_ENDPOINTS.adminSiteMetricsUser(person.id)}?days=${Math.max(days, 30)}`, { headers: getBearerAuthHeaders() })
      .then((r) => r.json())
      .then((b) => { if (!cancelled) { if (b.success) setRows(b.days); else setError(b.error || 'Couldn’t load.'); } })
      .catch(() => { if (!cancelled) setError('Couldn’t load.'); });
    return () => { cancelled = true; };
  }, [person.id, days]);
  return (
    <div className="asm-modal" role="dialog" aria-modal="true" aria-label={`Activity for ${person.email}`} onClick={onClose}>
      <div className="asm-modal-card" onClick={(e) => e.stopPropagation()}>
        <header>
          <div>
            <h3>{person.name || person.email}</h3>
            <small>{person.email} · {TIER_LABEL[person.tier] || person.tier}{person.brand ? ` · ${person.brand}` : ''}</small>
          </div>
          <button type="button" className="asm-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="asm-person-meta">
          <span>Signed up {dateText(person.signedUp)}</span>
          <span>Last sign-in {dateText(person.lastLogin)}</span>
          <span>Last active {ago(person.lastSeen)}</span>
        </div>
        {error ? <p className="asm-error">{error}</p> : null}
        {!rows && !error ? <p className="asm-muted">Loading…</p> : null}
        {rows ? (
          <table className="asm-table">
            <thead><tr><th>Day</th><th className="asm-num">Time on site</th><th className="asm-num">Visits</th><th>Hours (ET)</th><th>Pages</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.day}>
                  <td>{shortDay(d.day)}</td>
                  <td className="asm-num">{minutesText(d.minutes)}</td>
                  <td className="asm-num">{d.sessions}</td>
                  <td>{new Date(d.firstSeen).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}–{new Date(d.lastSeen).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}</td>
                  <td className="asm-pages">{Object.entries(d.pages || {}).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([p, m]) => `${pageName(p)} ${m}m`).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
}

const SORTS = {
  minutes: (a, b) => b.minutes - a.minutes,
  activeDays: (a, b) => b.activeDays - a.activeDays,
  sessions: (a, b) => b.sessions - a.sessions,
  lastSeen: (a, b) => String(b.lastSeen).localeCompare(String(a.lastSeen)),
  aiMessages: (a, b) => b.aiMessages - a.aiMessages,
  email: (a, b) => a.email.localeCompare(b.email),
};

export default function AdminSiteMetrics() {
  const [days, setDays] = useState(7);
  const [tier, setTier] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('minutes');
  const [person, setPerson] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ days: String(days), ...(tier ? { tier } : {}) });
      const res = await fetch(`${API_ENDPOINTS.adminSiteMetrics}?${qs}`, { headers: getBearerAuthHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || 'Couldn’t load site metrics.');
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days, tier]);

  useEffect(() => { load(); }, [load]);

  const people = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.people || [])
      .filter((p) => !q || p.email.toLowerCase().includes(q) || String(p.name || '').toLowerCase().includes(q))
      .sort(SORTS[sort]);
  }, [data, query, sort]);

  const t = data?.totals;
  const rangeLabel = RANGES.find((r) => r.days === days)?.label || `${days} days`;
  const devices = data?.devices || {};
  const deviceTotal = (devices.desktop || 0) + (devices.mobile || 0);

  const sortHeader = (key, label, className = '') => (
    <th className={className}>
      <button type="button" className={`asm-sort${sort === key ? ' is-active' : ''}`} onClick={() => setSort(key)} aria-pressed={sort === key}>{label}</button>
    </th>
  );

  return (
    <div className="asm">
      <header className="asm-head">
        <div>
          <h1>Site metrics</h1>
          <p>Who’s using RiskLo, how much, and where. Time counts while someone is actively using the site.</p>
        </div>
        <button type="button" className="asm-refresh" onClick={load} disabled={loading}>
          <IconRefresh size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      <div className="asm-filters">
        <div className="asm-pills" role="tablist" aria-label="Period">
          {RANGES.map((r) => (
            <button key={r.days} type="button" role="tab" aria-selected={days === r.days} className={days === r.days ? 'is-active' : ''} onClick={() => setDays(r.days)}>{r.label}</button>
          ))}
        </div>
        <select value={tier} onChange={(e) => setTier(e.target.value)} aria-label="Tier">
          {TIER_FILTERS.map((k) => <option key={k} value={k}>{k ? `${TIER_LABEL[k]} only` : 'All tiers'}</option>)}
        </select>
      </div>

      {error ? <p className="asm-error">{error}</p> : null}

      {t ? (
        <>
          <section className="asm-stats">
            <Stat label="Active today" value={num(t.activeToday)} />
            <Stat label="Active, 7 days" value={num(t.active7d)} />
            <Stat label="Active, 30 days" value={num(t.active30d)} />
            <Stat label="Total accounts" value={num(t.users)} sub={`+${num(t.newUsers7d)} this week · +${num(t.newUsers30d)} in 30 days`} />
            <Stat label={`Time on site, ${rangeLabel.toLowerCase()}`} value={minutesText(t.minutesInRange)} sub={`${num(t.sessionsInRange)} visits`} />
            <Stat label="Average per active person" value={minutesText(Math.round(t.avgMinutesPerActivePerson))} sub={`over ${rangeLabel.toLowerCase()}`} />
          </section>

          <section className="asm-card">
            <h2>Active people per day</h2>
            {data.daily.length ? (
              <div className="asm-chart" role="img" aria-label="Active people per day">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.daily} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid vertical={false} stroke="var(--asm-grid)" />
                    <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fill: 'var(--asm-muted)', fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={16} />
                    <YAxis allowDecimals={false} tick={{ fill: 'var(--asm-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<DayTooltip />} cursor={{ fill: 'var(--asm-hover)' }} />
                    <Bar dataKey="people" fill="var(--asm-series)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="asm-muted">No activity recorded in this period yet.</p>}
          </section>

          <div className="asm-grid">
            <section className="asm-card">
              <h2>By tier</h2>
              {data.byTier.length ? (
                <BarList
                  rows={data.byTier}
                  valueOf={(r) => r.people}
                  labelOf={(r) => TIER_LABEL[r.tier] || r.tier}
                  detailOf={(r) => `${num(r.people)} people · ${minutesText(r.minutes)}`}
                />
              ) : <p className="asm-muted">No activity yet.</p>}
            </section>
            <section className="asm-card">
              <h2>Most used pages</h2>
              {data.pages.length ? (
                <BarList
                  rows={data.pages.slice(0, 10)}
                  valueOf={(r) => r.minutes}
                  labelOf={(r) => pageName(r.page)}
                  detailOf={(r) => `${minutesText(r.minutes)} · ${num(r.people)} people`}
                />
              ) : <p className="asm-muted">No activity yet.</p>}
              {deviceTotal ? (
                <p className="asm-devices">
                  Desktop {Math.round(((devices.desktop || 0) / deviceTotal) * 100)}% · Mobile {Math.round(((devices.mobile || 0) / deviceTotal) * 100)}% of time on site
                </p>
              ) : null}
            </section>
          </div>

          <section className="asm-card">
            <div className="asm-people-head">
              <h2>People ({num(people.length)}{data.people.length >= 500 ? ', top 500' : ''})</h2>
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search email or name" aria-label="Search people" />
            </div>
            <div className="asm-table-wrap">
              <table className="asm-table">
                <thead>
                  <tr>
                    {sortHeader('email', 'Person')}
                    <th>Tier</th>
                    <th>Brand</th>
                    {sortHeader('minutes', 'Time on site', 'asm-num')}
                    {sortHeader('activeDays', 'Days active', 'asm-num')}
                    {sortHeader('sessions', 'Visits', 'asm-num')}
                    {sortHeader('aiMessages', 'AI questions', 'asm-num')}
                    <th>Most used</th>
                    {sortHeader('lastSeen', 'Last active')}
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.id} onClick={() => setPerson(p)} className="asm-row">
                      <td>
                        <button type="button" className="asm-person" onClick={(e) => { e.stopPropagation(); setPerson(p); }}>
                          <strong>{p.name || p.email}</strong>
                          {p.name ? <small>{p.email}</small> : null}
                        </button>
                      </td>
                      <td><span className={`asm-tier asm-tier--${p.tier}`}>{TIER_LABEL[p.tier] || p.tier}</span></td>
                      <td>{p.brand || '—'}</td>
                      <td className="asm-num">{minutesText(p.minutes)}</td>
                      <td className="asm-num">{p.activeDays}</td>
                      <td className="asm-num">{p.sessions}</td>
                      <td className="asm-num">{p.aiMessages || '—'}</td>
                      <td>{p.topPage ? pageName(p.topPage) : '—'}</td>
                      <td>{ago(p.lastSeen)}</td>
                    </tr>
                  ))}
                  {!people.length ? <tr><td colSpan={9} className="asm-muted">Nobody matches.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>
          <p className="asm-foot">Updated {ago(data.generatedAt)} · New York days · Tracking started when this page was added, so earlier days are empty.</p>
        </>
      ) : (!error ? <p className="asm-muted">Loading…</p> : null)}

      {person ? <PersonDetail person={person} days={days} onClose={() => setPerson(null)} /> : null}
    </div>
  );
}
